import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from "recharts";

interface Subcategory {
  id: string;
  name: string;
  category_id: string;
  category_name: string;
}

interface LabResultWithSubcategory {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string | null;
  created_at: string;
  subcategory_id: string | null;
  subcategory_name: string | null;
  category_name: string | null;
  _isPair?: boolean;
  _side?: 'direita' | 'esquerda';
  _isBloodPressure?: boolean;
  _bpSide?: 'sistolica' | 'diastolica';
}

type Period = "7" | "30" | "90" | "365" | "all";

export const SubcategoryChart = ({ 
  subcategory, 
  results, 
  period,
  pdfMode = false,
  pairBaseName
}: { 
  subcategory: Subcategory, 
  results: LabResultWithSubcategory[], 
  period: Period,
  pdfMode?: boolean,
  pairBaseName?: string
}) => {
  const getFilteredResults = () => {
    if (period === "all") return results;
    const days = parseInt(period);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return results.filter(result => {
      if (!result.collected_at) return false;
      return new Date(result.collected_at) >= cutoffDate;
    });
  };

  const filtered = getFilteredResults();
  
  if (filtered.length === 0) {
    return null;
  }

  const sortedResults = [...filtered].sort((a, b) => {
    const dateA = new Date(a.collected_at || a.created_at).getTime();
    const dateB = new Date(b.collected_at || b.created_at).getTime();
    return dateA - dateB;
  });

  // Verificar se é um par (Direita/Esquerda)
  const isPair = pairBaseName !== undefined && sortedResults.some(r => r._isPair);
  
  // Verificar se é pressão arterial (Sistólica/Diastólica)
  const isBloodPressure = sortedResults.some(r => r._isBloodPressure);
  
  // Separar dados por lado se for um par
  const direitaResults = isPair ? sortedResults.filter(r => r._side === 'direita') : [];
  const esquerdaResults = isPair ? sortedResults.filter(r => r._side === 'esquerda') : [];
  
  // Separar dados para pressão arterial
  const sistolicaResults = isBloodPressure ? sortedResults.filter(r => r._bpSide === 'sistolica') : [];
  const diastolicaResults = isBloodPressure ? sortedResults.filter(r => r._bpSide === 'diastolica') : [];
  
  // Detectar gráficos com poucos dados (< 2 pontos)
  const hasInsufficientData = (isPair || isBloodPressure)
    ? sortedResults.length < 2
    : sortedResults.length < 2;

  // Criar dados do gráfico
  const createChartData = (results: LabResultWithSubcategory[]) => {
    const dataMap = new Map<string, {
      date: string;
      fullDate: string;
      timestamp: number;
      direita?: number | null;
      esquerda?: number | null;
      sistolica?: number | null;
      diastolica?: number | null;
      value?: number | null;
      testName: string;
      unit: string | null;
    }>();

    results.forEach(r => {
      const dateObj = new Date(r.collected_at || r.created_at);
      const dateKey = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
      
      if (!dataMap.has(dateKey)) {
        dataMap.set(dateKey, {
          date: dateKey,
          fullDate: dateObj.toLocaleDateString('pt-BR'),
          timestamp: dateObj.getTime(),
          testName: r.test_name,
          unit: r.unit
        });
      }
      
      const entry = dataMap.get(dateKey)!;
      if (isPair && r._side) {
        if (r._side === 'direita') {
          entry.direita = r.value;
        } else {
          entry.esquerda = r.value;
        }
      } else if (isBloodPressure && r._bpSide) {
        if (r._bpSide === 'sistolica') {
          entry.sistolica = r.value;
        } else {
          entry.diastolica = r.value;
        }
      } else {
        entry.value = r.value;
      }
    });

    return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  const chartData = createChartData(sortedResults);

  const latest = sortedResults[sortedResults.length - 1];
  const hasRefRange = latest.ref_low !== null || latest.ref_high !== null;

  // Se há dados insuficientes, renderizar card simplificado (para ambos os modos)
  if (hasInsufficientData) {
    // Se for um par, pegar os últimos valores de cada lado
    const latestDireita = isPair && direitaResults.length > 0 ? direitaResults[direitaResults.length - 1] : null;
    const latestEsquerda = isPair && esquerdaResults.length > 0 ? esquerdaResults[esquerdaResults.length - 1] : null;
    const latestSistolica = isBloodPressure && sistolicaResults.length > 0 ? sistolicaResults[sistolicaResults.length - 1] : null;
    const latestDiastolica = isBloodPressure && diastolicaResults.length > 0 ? diastolicaResults[diastolicaResults.length - 1] : null;
    
    return (
      <Card className={pdfMode ? "w-full border shadow-sm print:shadow-none" : ""}>
        <CardHeader className={pdfMode ? "pb-3" : "p-4 pb-2"}>
          <CardTitle className={pdfMode ? "text-base font-semibold flex items-center justify-between" : "text-sm font-medium flex items-center justify-between"}>
            <span>{isPair && pairBaseName ? pairBaseName : subcategory.name}</span>
          </CardTitle>
          {!pdfMode && (
            <p className="text-xs text-muted-foreground mt-1">{subcategory.category_name}</p>
          )}
        </CardHeader>
        <CardContent 
          className="flex flex-col items-center justify-center" 
          style={pdfMode ? { height: '180px' } : { height: '200px' }}
        >
          {isBloodPressure && (latestSistolica || latestDiastolica) ? (
            <div className="w-full space-y-3">
              <div className="flex items-center justify-center gap-6">
                <div className="flex flex-col items-center">
                  <p className={pdfMode ? "text-xs mb-1" : "text-sm mb-1"} style={{ color: '#ef4444' }}>Sistólica</p>
                  <div className={pdfMode ? "text-2xl font-bold" : "text-3xl font-bold"} style={{ color: '#ef4444' }}>
                    {latestSistolica?.value ?? 'N/A'}
                  </div>
                </div>
                <div className={pdfMode ? "text-xl text-muted-foreground" : "text-2xl text-muted-foreground"}>/</div>
                <div className="flex flex-col items-center">
                  <p className={pdfMode ? "text-xs mb-1" : "text-sm mb-1"} style={{ color: '#3b82f6' }}>Diastólica</p>
                  <div className={pdfMode ? "text-2xl font-bold" : "text-3xl font-bold"} style={{ color: '#3b82f6' }}>
                    {latestDiastolica?.value ?? 'N/A'}
                  </div>
                </div>
              </div>
              <p className={pdfMode ? "text-[10px] text-muted-foreground text-center" : "text-xs text-muted-foreground text-center"}>
                {(latestSistolica?.value && latestDiastolica?.value) ? `${latestSistolica.value}/${latestDiastolica.value} mmHg` : ''} 
                {latestSistolica?.collected_at ? ` - ${new Date(latestSistolica.collected_at).toLocaleDateString('pt-BR')}` : ''}
              </p>
            </div>
          ) : isPair && (latestDireita || latestEsquerda) ? (
            <div className="w-full space-y-4">
              <div className="flex flex-col items-center">
                <p className={pdfMode ? "text-xs text-muted-foreground mb-1" : "text-sm text-muted-foreground mb-2"}>
                  Direita
                </p>
                <div className={pdfMode ? "text-2xl font-bold text-primary" : "text-3xl font-bold text-primary"}>
                  {latestDireita && latestDireita.value !== null && latestDireita.value !== undefined 
                    ? `${latestDireita.value} ${latestDireita.unit || ''}`
                    : 'N/A'}
                </div>
                {latestDireita && latestDireita.collected_at && (
                  <p className={pdfMode ? "text-[10px] text-muted-foreground mt-1" : "text-xs text-muted-foreground mt-1"}>
                    {new Date(latestDireita.collected_at).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-center">
                <p className={pdfMode ? "text-xs text-muted-foreground mb-1" : "text-sm text-muted-foreground mb-2"}>
                  Esquerda
                </p>
                <div className={pdfMode ? "text-2xl font-bold text-primary" : "text-3xl font-bold text-primary"}>
                  {latestEsquerda && latestEsquerda.value !== null && latestEsquerda.value !== undefined 
                    ? `${latestEsquerda.value} ${latestEsquerda.unit || ''}`
                    : 'N/A'}
                </div>
                {latestEsquerda && latestEsquerda.collected_at && (
                  <p className={pdfMode ? "text-[10px] text-muted-foreground mt-1" : "text-xs text-muted-foreground mt-1"}>
                    {new Date(latestEsquerda.collected_at).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            // Card normal (não é par)
            <>
              <div className={pdfMode ? "text-3xl font-bold text-primary" : "text-4xl font-bold text-primary"}>
                {latest.value !== null && latest.value !== undefined 
                  ? `${latest.value} ${latest.unit || ''}`
                  : 'N/A'}
              </div>
              <p className={pdfMode ? "text-xs text-muted-foreground mt-2" : "text-sm text-muted-foreground mt-3"}>
                {latest.collected_at 
                  ? `Medição de ${new Date(latest.collected_at).toLocaleDateString('pt-BR')}`
                  : 'Data não disponível'}
              </p>
              {hasRefRange && (
                <p className={pdfMode ? "text-xs text-muted-foreground mt-1" : "text-sm text-muted-foreground mt-2"}>
                  Referência: {latest.ref_low} - {latest.ref_high} {latest.unit}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Configurações específicas para PDF - DIMENSÕES ABSOLUTAS
  const chartConfig = pdfMode ? {
    fontSize: 9,
    strokeWidth: 1.5,
    dotRadius: 3,
    refLineWidth: 1.5,
  } : {
    fontSize: 10,
    strokeWidth: 2,
    dotRadius: 4,
    refLineWidth: 1.5,
  };

  // Pegar valores min/max para o domínio do eixo Y
  const getAllValues = () => {
    if (isPair) {
      const direitaValues = chartData.map(d => d.direita).filter((v): v is number => v !== null && v !== undefined);
      const esquerdaValues = chartData.map(d => d.esquerda).filter((v): v is number => v !== null && v !== undefined);
      return [...direitaValues, ...esquerdaValues];
    }
    return chartData.map(d => d.value).filter((v): v is number => v !== null && v !== undefined);
  };

  const allValues = getAllValues();
  const refLow = latest.ref_low;
  const refHigh = latest.ref_high;
  
  const yAxisDomain = (() => {
    const values = [...allValues];
    if (refLow !== null) values.push(refLow);
    if (refHigh !== null) values.push(refHigh);
    
    if (values.length === 0) return [0, 100];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1 || 1;
    
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  })();

  return (
    <Card className={pdfMode ? "shadow-none border-border/50 p-0" : ""}>
      <CardHeader className={pdfMode ? "p-1.5 pb-1" : "p-4 pb-2"}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className={pdfMode ? "text-xs font-medium" : "text-sm font-medium"}>
              {isPair && pairBaseName ? pairBaseName : subcategory.name}
            </CardTitle>
            <p className={pdfMode ? "text-[9px] text-muted-foreground mt-0.5" : "text-xs text-muted-foreground mt-1"}>
              {subcategory.category_name}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className={pdfMode ? "p-0" : "p-4 pt-2"}>
        {pdfMode ? (
          <div className="w-full flex items-center justify-center" style={{ height: '180px' }}>
            <LineChart 
              data={chartData} 
              width={680}
              height={180}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="hsl(var(--border))" 
                opacity={0.3}
              />
              <XAxis
                dataKey="date"
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 8 }}
                height={40}
                tickMargin={8}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 9 }}
                tickMargin={8}
                domain={yAxisDomain}
              />
              {refLow !== null && (
                <ReferenceLine
                  y={refLow}
                  stroke="hsl(var(--chart-2))"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  label={{
                    value: "Mín",
                    position: "insideBottomLeft",
                    fontSize: 7,
                    fill: "hsl(var(--chart-2))",
                  }}
                />
              )}
              {refHigh !== null && (
                <ReferenceLine
                  y={refHigh}
                  stroke="hsl(var(--chart-1))"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  label={{
                    value: "Máx",
                    position: "insideTopLeft",
                    fontSize: 7,
                    fill: "hsl(var(--chart-1))",
                  }}
                />
              )}
              {isBloodPressure ? (
                <>
                  <Line type="monotone" dataKey="sistolica" stroke="#ef4444" strokeWidth={2} dot={{ fill: "#ef4444", r: 3 }} activeDot={{ r: 4 }} name="Sistólica" />
                  <Line type="monotone" dataKey="diastolica" stroke="#3b82f6" strokeWidth={2} dot={{ fill: "#3b82f6", r: 3 }} activeDot={{ r: 4 }} name="Diastólica" />
                  <Legend wrapperStyle={{ fontSize: 9 }} iconType="line" />
                </>
              ) : isPair ? (
                <>
                  <Line type="monotone" dataKey="direita" stroke="#3b82f6" strokeWidth={2} dot={{ fill: "#3b82f6", r: 3 }} activeDot={{ r: 4 }} name="Direita" />
                  <Line type="monotone" dataKey="esquerda" stroke="#ef4444" strokeWidth={2} dot={{ fill: "#ef4444", r: 3 }} activeDot={{ r: 4 }} name="Esquerda" />
                  <Legend wrapperStyle={{ fontSize: 9 }} iconType="line" formatter={(value) => value === "Direita" ? "Direita" : value === "Esquerda" ? "Esquerda" : value} />
                </>
              ) : (
                <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))", r: 3 }} activeDot={{ r: 4 }} />
              )}
            </LineChart>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 11 }}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 11 }}
                domain={yAxisDomain}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--background))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "6px",
                }}
                formatter={(value: number, name: string) => {
                  if (value === null || value === undefined) return [null, name];
                  const label = name === "direita" ? "Direita" : name === "esquerda" ? "Esquerda" 
                    : name === "sistolica" ? "Sistólica" : name === "diastolica" ? "Diastólica" : "Valor";
                  return [`${value.toFixed(2)} ${chartData[0]?.unit || ""}`, label];
                }}
                  labelFormatter={(label, payload) => {
                    if (payload && payload.length > 0 && payload[0].payload.timestamp) {
                      const date = new Date(payload[0].payload.timestamp);
                      return date.toLocaleDateString("pt-BR");
                    }
                    if (payload && payload.length > 0 && payload[0].payload.fullDate) {
                      return payload[0].payload.fullDate;
                    }
                    return label;
                  }}
              />
              {refLow !== null && (
                <ReferenceLine
                  y={refLow}
                  stroke="hsl(var(--chart-2))"
                  strokeDasharray="3 3"
                  strokeWidth={1.5}
                  label={{
                    value: "Mínimo",
                    position: "insideBottomLeft",
                    fontSize: 10,
                    fill: "hsl(var(--chart-2))",
                  }}
                />
              )}
              {refHigh !== null && (
                <ReferenceLine
                  y={refHigh}
                  stroke="hsl(var(--chart-1))"
                  strokeDasharray="3 3"
                  strokeWidth={1.5}
                  label={{
                    value: "Máximo",
                    position: "insideTopLeft",
                    fontSize: 10,
                    fill: "hsl(var(--chart-1))",
                  }}
                />
              )}
              {isBloodPressure ? (
                <>
                  <Line type="monotone" dataKey="sistolica" stroke="#ef4444" strokeWidth={2} dot={{ fill: "#ef4444", r: 4 }} activeDot={{ r: 6 }} name="Sistólica" connectNulls />
                  <Line type="monotone" dataKey="diastolica" stroke="#3b82f6" strokeWidth={2} dot={{ fill: "#3b82f6", r: 4 }} activeDot={{ r: 6 }} name="Diastólica" connectNulls />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="line" />
                </>
              ) : isPair ? (
                <>
                  <Line type="monotone" dataKey="direita" stroke="#3b82f6" strokeWidth={2} dot={{ fill: "#3b82f6", r: 4 }} activeDot={{ r: 6 }} name="Direita" />
                  <Line type="monotone" dataKey="esquerda" stroke="#ef4444" strokeWidth={2} dot={{ fill: "#ef4444", r: 4 }} activeDot={{ r: 6 }} name="Esquerda" />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="line" formatter={(value) => value === "Direita" ? "Direita" : value === "Esquerda" ? "Esquerda" : value} />
                </>
              ) : (
                <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))", r: 4 }} activeDot={{ r: 6 }} />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}

        {isBloodPressure ? (
          <div className={pdfMode ? "mt-1 pt-1 border-t" : "mt-4 pt-4 border-t text-sm"}>
            <p className={pdfMode ? "text-muted-foreground text-[9px]" : "text-muted-foreground text-xs"}>Última Medição</p>
            {(() => {
              const latSis = sistolicaResults[sistolicaResults.length - 1];
              const latDia = diastolicaResults[diastolicaResults.length - 1];
              const sVal = latSis?.value ?? '—';
              const dVal = latDia?.value ?? '—';
              const dateStr = latSis?.collected_at ? new Date(latSis.collected_at).toLocaleDateString('pt-BR') : latDia?.collected_at ? new Date(latDia.collected_at).toLocaleDateString('pt-BR') : '';
              return (
                <>
                  <p className={pdfMode ? "font-semibold mt-0.5 text-[9px]" : "font-semibold mt-1"}>
                    <span style={{ color: '#ef4444' }}>{sVal}</span>
                    <span className="text-muted-foreground"> / </span>
                    <span style={{ color: '#3b82f6' }}>{dVal}</span>
                    <span className="text-muted-foreground ml-1">mmHg</span>
                  </p>
                  {dateStr && <p className={pdfMode ? "text-[9px] text-muted-foreground mt-0.5" : "text-xs text-muted-foreground mt-0.5"}>{dateStr}</p>}
                </>
              );
            })()}
          </div>
        ) : isPair ? (
          <div className={pdfMode ? "mt-1 pt-1 border-t grid grid-cols-2 gap-2" : "mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm"}>
            <div>
              <p className={pdfMode ? "text-muted-foreground text-[9px]" : "text-muted-foreground text-xs"}>Última Medição - Direita</p>
              {(() => {
                const latestDireita = direitaResults[direitaResults.length - 1];
                return latestDireita ? (
                  <>
                    <p className={pdfMode ? "font-semibold mt-0.5 text-[9px]" : "font-semibold mt-1"}>
                      {latestDireita.value} {latestDireita.unit}
                    </p>
                    <p className={pdfMode ? "text-[9px] text-muted-foreground mt-0.5" : "text-xs text-muted-foreground mt-0.5"}>
                      {new Date(latestDireita.collected_at || latestDireita.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </>
                ) : (
                  <p className={pdfMode ? "text-[9px] text-muted-foreground mt-0.5" : "text-xs text-muted-foreground mt-0.5"}>N/A</p>
                );
              })()}
            </div>
            <div>
              <p className={pdfMode ? "text-muted-foreground text-[9px]" : "text-muted-foreground text-xs"}>Última Medição - Esquerda</p>
              {(() => {
                const latestEsquerda = esquerdaResults[esquerdaResults.length - 1];
                return latestEsquerda ? (
                  <>
                    <p className={pdfMode ? "font-semibold mt-0.5 text-[9px]" : "font-semibold mt-1"}>
                      {latestEsquerda.value} {latestEsquerda.unit}
                    </p>
                    <p className={pdfMode ? "text-[9px] text-muted-foreground mt-0.5" : "text-xs text-muted-foreground mt-0.5"}>
                      {new Date(latestEsquerda.collected_at || latestEsquerda.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </>
                ) : (
                  <p className={pdfMode ? "text-[9px] text-muted-foreground mt-0.5" : "text-xs text-muted-foreground mt-0.5"}>N/A</p>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className={pdfMode ? "mt-1 pt-1 border-t grid grid-cols-2 gap-2" : "mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm"}>
            <div>
              <p className={pdfMode ? "text-muted-foreground text-[9px]" : "text-muted-foreground text-xs"}>Última Medição</p>
              <p className={pdfMode ? "font-semibold mt-0.5 text-[9px]" : "font-semibold mt-1"}>
                {latest.value} {latest.unit}
              </p>
              <p className={pdfMode ? "text-[9px] text-muted-foreground mt-0.5" : "text-xs text-muted-foreground mt-0.5"}>
                {new Date(latest.collected_at || latest.created_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
            
            {hasRefRange && (
              <div>
                <p className={pdfMode ? "text-muted-foreground text-[9px]" : "text-muted-foreground text-xs"}>Faixa de Referência</p>
                <p className={pdfMode ? "font-semibold mt-0.5 text-[9px]" : "font-semibold mt-1"}>
                  {latest.ref_low !== null ? latest.ref_low : '-'} - {latest.ref_high !== null ? latest.ref_high : '-'} {latest.unit}
                </p>
                <p className={pdfMode ? "text-[9px] mt-0.5" : "text-xs mt-0.5"}>
                  {latest.value !== null && hasRefRange && (
                    latest.ref_low !== null && latest.value < latest.ref_low 
                      ? <span className="text-destructive">⬇ Abaixo</span>
                      : latest.ref_high !== null && latest.value > latest.ref_high 
                        ? <span className="text-destructive">⬆ Acima</span>
                        : <span className="text-green-600">✓ Normal</span>
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
